import React, { useEffect, useState } from "react";
import { MousePointerClick, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const CHANNEL_COLORS = {
  whatsapp: "#25D366",
  email: "#0EA5A9",
  phone: "#F97316",
  contact: "#94A3B8",
};

const ProductCatalogStats = ({ refreshKey = 0 }) => {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/products/me/stats")
      .then(({ data }) => setStats(data))
      .catch(() => setStats({ products: [], by_kind: [], total_clicks: 0, recent: [] }));
  }, [refreshKey]);

  if (!stats) return null;
  const hasProducts = (stats.products || []).length > 0;

  return (
    <section className="mt-10 pt-8 border-t border-gray-100" data-testid="catalog-stats">
      <div className="flex items-start gap-3 flex-wrap">
        <div>
          <p className="eyebrow text-orange">{t.catalog.statsTitle}</p>
          <h2 className="font-display text-3xl text-teal-deep mt-1 leading-tight">{t.catalog.statsTitle}</h2>
          <p className="text-sm text-teal-soft mt-2">{t.catalog.statsSubtitle}</p>
        </div>
      </div>

      {!hasProducts ? (
        <div className="mt-6 bg-cream rounded-2xl border border-dashed border-gray-300 p-8 text-center">
          <p className="text-teal-soft text-sm">{t.catalog.statsEmpty}</p>
        </div>
      ) : (
        <>
          {/* Top stats card */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 rounded-2xl bg-orange/10 border-2 border-orange/25">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full bg-orange text-white flex items-center justify-center shadow-lg shadow-orange/30">
                  <MousePointerClick size={18} />
                </span>
                <div>
                  <div className="text-xs text-teal-soft uppercase tracking-wider font-bold">{t.catalog.statsTotal}</div>
                  <div className="font-display text-3xl text-teal-deep leading-none mt-1" data-testid="catalog-total-clicks">
                    {stats.total_clicks}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-5 rounded-2xl bg-teal/5 border-2 border-teal/15">
              <div className="text-xs text-teal-soft uppercase tracking-wider font-bold flex items-center gap-2">
                <TrendingUp size={14} /> {t.catalog.statsByChannel}
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                {(stats.by_kind || []).map((k) => (
                  <div key={k.kind} className="bg-white rounded-xl px-2 py-2 border border-gray-100">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-teal-soft truncate">
                      {k.kind === "whatsapp" ? t.catalog.contactWa : k.kind === "email" ? t.catalog.contactEmail : k.kind === "phone" ? t.catalog.contactPhone : "—"}
                    </div>
                    <div className="font-display text-xl mt-0.5" style={{ color: CHANNEL_COLORS[k.kind] || "#0EA5A9" }} data-testid={`catalog-stat-${k.kind}`}>
                      {k.count}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="mt-6 bg-white rounded-2xl border border-gray-200 p-5">
            <div className="text-xs uppercase tracking-wider font-bold text-teal-soft mb-3">
              {t.catalog.statsColProduct} · {t.catalog.statsColClicks}
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={stats.products.map((p) => ({ name: p.title, clicks: p.click_count }))}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#0F766E" }} interval={0} angle={-15} textAnchor="end" height={60} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                  <Tooltip cursor={{ fill: "rgba(15,118,110,0.05)" }} />
                  <Bar dataKey="clicks" radius={[8, 8, 0, 0]}>
                    {stats.products.map((p, i) => (
                      <Cell key={p.id} fill={i === 0 ? "#F97316" : "#0EA5A9"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Table */}
          <div className="mt-6 bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-cream">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-teal">{t.catalog.statsColProduct}</th>
                  <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-wider text-teal">{t.catalog.statsColClicks}</th>
                </tr>
              </thead>
              <tbody>
                {stats.products.map((p) => (
                  <tr key={p.id} className="border-t border-gray-100" data-testid={`catalog-stats-row-${p.id}`}>
                    <td className="px-5 py-3 text-teal-deep">{p.title}</td>
                    <td className="px-5 py-3 text-right font-display text-lg text-orange">{p.click_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Recent activity */}
          <div className="mt-6">
            <div className="text-xs uppercase tracking-wider font-bold text-teal-soft mb-2">{t.catalog.statsRecent}</div>
            {(stats.recent || []).length === 0 ? (
              <p className="text-sm text-teal-soft italic">{t.catalog.statsRecentEmpty}</p>
            ) : (
              <ul className="space-y-2">
                {stats.recent.map((r) => (
                  <li key={r.id} className="flex items-center gap-3 text-sm bg-cream rounded-xl px-4 py-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ background: CHANNEL_COLORS[r.kind] || "#94A3B8" }}
                    />
                    <span className="text-teal-deep font-semibold truncate">{r.product_title}</span>
                    <span className="text-xs text-teal-soft uppercase tracking-wider">
                      {r.kind === "whatsapp" ? t.catalog.contactWa : r.kind === "email" ? t.catalog.contactEmail : r.kind === "phone" ? t.catalog.contactPhone : "—"}
                    </span>
                    <span className="text-xs text-teal-soft ml-auto">{(r.created_at || "").slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default ProductCatalogStats;
